import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { notesApi, } from "@/api/browser.ts";
import { projectsApi } from "@/api/projects.ts";
import { projectQuery } from "@/features/projects/ProjectPage.tsx";
import { Alert, Button, Spinner } from "@/ui/primitives.tsx";
import { useConfirm } from "@/ui/ConfirmDialog.tsx";
import { useApiError } from "@/ui/useApiError.ts";
import { cn } from "@/lib/cn.ts";

/** memory.md is organized past this size (the server's MEMORY_SOFT_BYTES): a soft mark, nothing refuses more. */
const SOFT_BYTES = 20_000;
const kb = (n: number) => `${(n / 1000).toLocaleString(undefined, { maximumFractionDigits: 1 })} KB`;

/** Project memory: a markdown file in the workspace that attached chats read and keep current, its journal (the
 *  history moved out of it) and its copies (one before every rewrite). In the Chat section (chatSection) it is the old
 *  shared file that conversations no longer read, kept for reference. */
export function MemoryPanel({ projectId, canEdit, chatSection = false }: { projectId: string; canEdit: boolean; chatSection?: boolean }) {
  const { t } = useTranslation();
  const [tab, setTab] = useState<"memory" | "journal" | "backups">("memory");
  return (
    <div className={cn("flex flex-col text-sm", chatSection ? "min-h-0 flex-1" : "h-full")}>
      {!chatSection && (
        <div className="flex gap-1 border-b border-border px-2 pt-2">
          {(["memory", "journal", "backups"] as const).map((k) => (
            <button key={k} type="button" onClick={() => setTab(k)} className={cn("rounded-t-md px-3 py-1.5 text-xs", tab === k ? "bg-surface-2 font-medium text-fg" : "text-fg-muted hover:text-fg")}>{t(`memory.tabs.${k}`)}</button>
          ))}
        </div>
      )}
      {tab === "memory" || chatSection ? <MemoryTab projectId={projectId} canEdit={canEdit} chatSection={chatSection} /> : <FilesTab projectId={projectId} kind={tab} canEdit={canEdit} onRestored={() => setTab("memory")} />}
    </div>
  );
}

function MemoryTab({ projectId, canEdit, chatSection }: { projectId: string; canEdit: boolean; chatSection: boolean }) {
  const { t } = useTranslation();
  const toMsg = useApiError();
  const qc = useQueryClient();
  const [organizing, setOrganizing] = useState<number | null>(null);
  // while the organizer works, the file is re-read until it changes (or two minutes pass)
  const q = useQuery({ queryKey: ["memory", projectId], queryFn: () => notesApi.memory(projectId), refetchInterval: organizing && Date.now() - organizing < 120_000 ? 5000 : false });
  const project = useQuery(projectQuery(projectId)).data;
  const [draft, setDraft] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [startContent, setStartContent] = useState<string | null>(null);
  const save = useMutation({ mutationFn: (c: string) => notesApi.saveMemory(projectId, c), onSuccess: (d) => { qc.setQueryData(["memory", projectId], d); setDraft(null); } });
  const setDefault = useMutation({ mutationFn: (v: boolean) => projectsApi.update(projectId, { memoryDefault: v }), onSuccess: (p) => qc.setQueryData(["projects", projectId], p) });
  const organize = useMutation({
    mutationFn: () => notesApi.organize(projectId),
    onSuccess: (r) => {
      setNote(t(`memory.organize.${r.status}`));
      if (r.status === "started" || r.status === "queued") { setOrganizing(Date.now()); setStartContent(q.data?.content ?? ""); }
    },
  });
  // the organizer finished: the file changed under us
  useEffect(() => {
    if (!organizing || startContent === null || !q.data || q.data.content === startContent) return;
    setOrganizing(null);
    setStartContent(null);
    setNote(t("memory.organize.done"));
    void qc.invalidateQueries({ queryKey: ["memory-files", projectId] });
  }, [q.data?.content]);
  if (q.isLoading) return <div className="p-3"><Spinner /></div>;
  const value = draft ?? q.data?.content ?? "";
  const dirty = draft !== null && draft !== (q.data?.content ?? "");
  const bytes = new TextEncoder().encode(value).length;
  return (
    <>
      <div className="space-y-2 border-b border-border p-3">
        <p className="text-xs text-fg-muted">{chatSection ? t("chatMemory.oldHint") : t("memory.intro")} <code dir="ltr" className="rounded bg-surface-2 px-1">{q.data?.path}</code></p>
        {project && project.myRole === "owner" && !chatSection && (
          <label className="flex items-center gap-2 text-xs">
            <input type="checkbox" checked={project.memoryDefault} disabled={setDefault.isPending} onChange={(e) => setDefault.mutate(e.target.checked)} /> {t("memory.attachNew")}
          </label>
        )}
        {(save.error || setDefault.error || organize.error) && <Alert tone="danger">{toMsg(save.error ?? setDefault.error ?? organize.error)}</Alert>}
        {note && <Alert tone="info" className="flex items-center gap-2">{organizing && <Spinner />}<span>{note}</span></Alert>}
      </div>
      <textarea dir="auto" className="min-h-0 flex-1 resize-none bg-bg p-3 font-mono text-sm leading-6 focus:outline-none" placeholder={t("memory.placeholder")} value={value} readOnly={!canEdit} onChange={(e) => setDraft(e.target.value)} />
      <div className="flex flex-wrap items-center justify-between gap-2 border-t border-border px-3 py-1.5 text-[11px] text-fg-muted">
        <span dir="auto" className={cn(bytes > SOFT_BYTES && "font-medium text-warn")}>{t("memory.size", { size: kb(bytes), soft: kb(SOFT_BYTES) })}</span>
        <div className="flex gap-2">
          {canEdit && !chatSection && <Button size="sm" variant="secondary" loading={organize.isPending} disabled={!!organizing || dirty || !value.trim()} onClick={() => organize.mutate()}>{t("memory.organize.button")}</Button>}
          {canEdit && <Button size="sm" disabled={!dirty} loading={save.isPending} onClick={() => save.mutate(value)}>{t("common.save")}</Button>}
        </div>
      </div>
    </>
  );
}

/** The journal (history moved out of memory.md, team hand-overs) or the copies kept before each rewrite. */
function FilesTab({ projectId, kind, canEdit, onRestored }: { projectId: string; kind: "journal" | "backups"; canEdit: boolean; onRestored: () => void }) {
  const { t, i18n } = useTranslation();
  const toMsg = useApiError();
  const qc = useQueryClient();
  const { confirm } = useConfirm();
  const [open, setOpen] = useState<string | null>(null);
  const files = useQuery({ queryKey: ["memory-files", projectId, kind], queryFn: () => notesApi.memoryFiles(projectId, kind) });
  const file = useQuery({ queryKey: ["memory-file", projectId, kind, open], queryFn: () => notesApi.memoryFile(projectId, kind, open!), enabled: !!open });
  const restore = useMutation({ mutationFn: (name: string) => notesApi.restore(projectId, name), onSuccess: (d) => { qc.setQueryData(["memory", projectId], d); void qc.invalidateQueries({ queryKey: ["memory-files", projectId] }); onRestored(); } });
  const fmt = (iso: string) => new Intl.DateTimeFormat(i18n.language, { dateStyle: "medium", timeStyle: "short" }).format(new Date(iso));
  if (files.isLoading) return <div className="p-3"><Spinner /></div>;
  const list = files.data ?? [];
  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <p className="border-b border-border p-3 text-xs text-fg-muted">{t(`memory.${kind}Intro`)}</p>
      {restore.error && <Alert tone="danger" className="m-3">{toMsg(restore.error)}</Alert>}
      {!list.length && <p className="p-3 text-xs text-fg-muted">{t(`memory.${kind}Empty`)}</p>}
      <ul className={cn("overflow-y-auto border-b border-border", open ? "max-h-40" : "min-h-0 flex-1")}>
        {list.map((f) => (
          <li key={f.name}>
            <button type="button" onClick={() => setOpen(open === f.name ? null : f.name)} className={cn("flex w-full items-center justify-between gap-2 px-3 py-2 text-start text-xs hover:bg-surface-2", open === f.name && "bg-surface-2")}>
              <span dir="ltr" className="truncate font-mono">{kind === "journal" ? f.name.replace(/\.md$/, "") : fmt(f.at)}</span>
              <span className="shrink-0 text-fg-muted">{kb(f.size)}</span>
            </button>
          </li>
        ))}
      </ul>
      {open && (
        <div className="flex min-h-0 flex-1 flex-col">
          {file.isLoading ? <div className="p-3"><Spinner /></div> : <pre dir="auto" className="min-h-0 flex-1 overflow-auto whitespace-pre-wrap bg-bg p-3 font-mono text-xs leading-5">{file.data?.content ?? ""}</pre>}
          {kind === "backups" && canEdit && (
            <div className="flex justify-end border-t border-border px-3 py-1.5">
              <Button size="sm" variant="secondary" loading={restore.isPending} onClick={async () => { if (await confirm({ title: t("memory.restoreTitle"), message: t("memory.restoreMsg"), confirmLabel: t("memory.restore") })) restore.mutate(open); }}>{t("memory.restore")}</Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
